/**
 * `getJson(url)` — the single `fetch`-based JSON helper (P5-6 / MR-48) that
 * replaces the widget's hand-rolled `XMLHttpRequest` / pegasus HTTP code.
 *
 * It reproduces the "Family A" contract of the three byte-identical
 * `retrieveShowData` XHR wrappers (v4 Player, v3 Player, v2 V2Player) so the
 * MR-49 body-swap is behaviour-preserving:
 *
 *     status === 200  ->  resolve( parsed JSON )
 *     anything else   ->  reject( new Error(`${statusText} (${status})`) )
 *
 * The 200-exact gate (NOT `res.ok`, which is 200-299) is deliberate: the XHR
 * wrappers branch on `this.status === 200`, so a 2xx-non-200 (201/204) must
 * reject here too — faithful to the code being replaced.
 *
 * How the Family A callers observe a failure (matters for the MR-49 swap):
 *   - v4/v3 `Player.setShowTitle` calls `retrieveShowData().then(...)` with NO
 *     `.catch`, so any rejection is an unhandled rejection — no user-visible
 *     effect beyond the show title staying unset.
 *   - v2 `V2Player.onReady` DOES catch: `.catch(this.handleShowDataError)` sets
 *     `this.errorMessage = error.message` — a user-visible field. So the error's
 *     message/shape IS observed on the v2 path.
 *
 * Two real fetch-vs-XHR divergences on the failure paths (both edge cases):
 *   - malformed JSON on a 200: `res.json()` rejects, so `getJson` REJECTS. The
 *     XHR `retrieveShowData` behaved differently: its `JSON.parse` throws inside
 *     the readystate handler AFTER the executor returned, so that Promise never
 *     settles (`.then`/`.catch` never fire) — but the throw is NOT swallowed, it
 *     surfaces as an uncaught exception (console + `window.onerror`); the old path
 *     was therefore not telemetry-silent either. The pegasus `get()` used by
 *     app.vue instead resolves `null`. Post-MR-49, v2's malformed-200 goes from an
 *     endless loading spinner to `isLoading=false` + a raw parser message shown in
 *     `errorMessage` (strictly better — a hang becomes a surfaced error). For v4/v3
 *     (no `.catch` on setShowTitle) the never-settling promise becomes a settled-
 *     but-rejected one: the diagnostic just moves CHANNEL, from `window.onerror`
 *     (old uncaught throw) to `window.onunhandledrejection` (new unhandled reject)
 *     — no user-visible effect, no telemetry conjured from nothing, not a
 *     functional regression. MR-50 handles the app.vue path (the loadData crux).
 *   - network/DNS/CORS failure: XHR reaches `readyState 4` with `status 0` →
 *     `reject(new Error(' (0)'))`; `fetch` rejects with a native
 *     `TypeError('Failed to fetch')`, which `getJson` propagates unchanged. So
 *     the error TYPE and MESSAGE differ; on the v2 path `errorMessage` changes
 *     from ' (0)' to 'Failed to fetch'. fetch cannot cleanly synthesize the
 *     ' (0)' shape without over-engineering; both are opaque diagnostics.
 *
 * NOT a divergence: under HTTP/2 the reason phrase is dropped, so `statusText`
 * is empty and the error reads ' (404)' — but XHR's `statusText` is ALSO empty
 * under HTTP/2, so both APIs produce the same string. Behavior-preserving.
 *
 * `getJson` consumes an ALREADY-BUILT, security-hardened URL (from
 * `src/utils/api.js buildUrl` + the `src/config/hosts.js` allow-lists); it does
 * no URL construction of its own.
 *
 * ES2015 only (no optional-chaining / nullish / object-spread): the shared
 * `src/` bundle is pinned pre-ES2018 (VUE3_MIGRATION_PLAN Phase 5, Locked #3).
 */
// eslint-disable-next-line import/prefer-default-export
export function getJson(url) {
  return fetch(url).then((res) => {
    if (res.status === 200) {
      return res.json();
    }
    throw new Error(`${res.statusText} (${res.status})`);
  });
}
